import type {
  StoneFlickingConfig,
  StoneFlickingState,
} from "@cloudnest/augmented-stone-flicking-engine";
import {
  applyResolveRound,
  applySkipFlick,
  applySubmitFlick,
  applySurrender,
  buildCoreConfig,
  validateResolveRound,
  validateSkipFlick,
  validateSubmitFlick,
  validateSurrender,
} from "@cloudnest/augmented-stone-flicking-engine";
import type { RoundInput } from "@cloudnest/stone-flicking-core-engine/types";
import { createSeededProvider } from "@cloudnest/stone-flicking-core-engine/utils";
import { type AsfReplayEvent, toAsfReplayEvent } from "./event-adapter";
import type { AsfReplayFixtureEvent } from "./replay-fixtures.generated";

export interface AsfReplaySnapshot {
  readonly index: number;
  readonly event: AsfReplayFixtureEvent;
  readonly replayEvent: AsfReplayEvent;
  readonly state: StoneFlickingState;
  readonly config: StoneFlickingConfig;
}

interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
}

type SerializedPendingInputs = Readonly<Record<string, RoundInput | null>>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hydrateState(state: StoneFlickingState): StoneFlickingState {
  const pendingInputs = state.coreState.pendingInputs as unknown;
  if (pendingInputs instanceof Map) return state;

  const entries = isRecord(pendingInputs)
    ? (Object.entries(pendingInputs as SerializedPendingInputs) as Array<
        [string, RoundInput | null]
      >)
    : [];
  return {
    ...state,
    coreState: {
      ...state.coreState,
      pendingInputs: new Map<string, RoundInput | null>(entries),
    },
  };
}

function requireState(
  state: StoneFlickingState | null,
  eventType: AsfReplayEvent["eventType"],
): StoneFlickingState {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function requireConfig(
  config: StoneFlickingConfig | null,
  eventType: AsfReplayEvent["eventType"],
): StoneFlickingConfig {
  if (config === null) {
    throw new Error(`${eventType} before init config`);
  }
  return config;
}

function ensureValid(label: string, result: ValidationResult): void {
  if (!result.valid) {
    throw new Error(
      `${label} rejected: ${result.error ?? "Validation failed"}`,
    );
  }
}

function applyReplayEvent(
  state: StoneFlickingState | null,
  config: StoneFlickingConfig | null,
  event: AsfReplayEvent,
): {
  readonly state: StoneFlickingState;
  readonly config: StoneFlickingConfig;
} {
  switch (event.eventType) {
    case "init":
      return {
        state: hydrateState(event.payload.state),
        config: event.payload.config,
      };

    case "action": {
      const current = requireState(state, event.eventType);
      const currentConfig = requireConfig(config, event.eventType);
      const coreConfig = buildCoreConfig(currentConfig);
      const action = event.payload;

      switch (action.type) {
        case "SUBMIT_FLICK":
          ensureValid(
            "SUBMIT_FLICK",
            validateSubmitFlick(current, action, coreConfig),
          );
          return {
            state: applySubmitFlick(current, action),
            config: currentConfig,
          };
        case "SKIP_FLICK":
          ensureValid("SKIP_FLICK", validateSkipFlick(current, action));
          return {
            state: applySkipFlick(current, action),
            config: currentConfig,
          };
        case "RESOLVE_ROUND":
          ensureValid("RESOLVE_ROUND", validateResolveRound(current, action));
          return {
            state: applyResolveRound(
              current,
              coreConfig,
              action.randomSeed === undefined
                ? undefined
                : createSeededProvider(action.randomSeed),
            ),
            config: currentConfig,
          };
        case "SURRENDER":
          ensureValid("SURRENDER", validateSurrender(current, action));
          return {
            state: applySurrender(current, action),
            config: currentConfig,
          };
      }
    }
  }
}

export function buildAugmentedStoneFlickingReplaySnapshots(
  events: readonly AsfReplayFixtureEvent[],
): readonly AsfReplaySnapshot[] {
  const snapshots: AsfReplaySnapshot[] = [];
  let state: StoneFlickingState | null = null;
  let config: StoneFlickingConfig | null = null;

  for (const event of events) {
    const replayEvent = toAsfReplayEvent(event);
    const next = applyReplayEvent(state, config, replayEvent);
    state = next.state;
    config = next.config;
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
      config,
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty ASF event log");
  }

  return snapshots;
}
